import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, Edit, Trash2, Search, Upload, X, Images } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';
import ImageUpload from '../../components/ImageUpload';
import AdminProductsBulk from './AdminProductsBulk';

const PAGE_SIZE = 24;
const SORT_OPTIONS = {
  newest: { label: 'Date added: Newest first', sort_by: 'created_at', sort_order: 'desc' },
  oldest: { label: 'Date added: Oldest first', sort_by: 'created_at', sort_order: 'asc' },
  name_asc: { label: 'Name: A–Z', sort_by: 'name', sort_order: 'asc' },
  name_desc: { label: 'Name: Z–A', sort_by: 'name', sort_order: 'desc' },
};

const normalizeImageList = (...groups) => {
  const seen = new Set();
  return groups
    .flat()
    .map((value) => (value || '').trim())
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const getProductImageList = (product) => normalizeImageList([product?.image_url], product?.images || []);
const getAdditionalProductImages = (product) => (
  normalizeImageList(product?.images || []).filter((url) => url !== (product?.image_url || '').trim())
);

const AdminProducts = () => {
  const { toast } = useToast();
  const galleryFileInputRef = useRef(null);
  const productRequestRef = useRef(0);
  const [products, setProducts] = useState([]);
  const [bulkView, setBulkView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortMode, setSortMode] = useState('name_asc');
  const [totalProducts, setTotalProducts] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    category_en: '',
    category_ar: '',
    description_en: '',
    description_ar: '',
    price_min: '',
    price_max: '',
    image_url: '',
    stock_quantity: '10'
  });
  const [specs, setSpecs] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryImageUrl, setGalleryImageUrl] = useState('');
  const [galleryUploading, setGalleryUploading] = useState(false);

  const loadProducts = async () => {
    const requestId = ++productRequestRef.current;
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        sort_by: SORT_OPTIONS[sortMode].sort_by,
        sort_order: SORT_OPTIONS[sortMode].sort_order,
        sort_lang: 'en',
      };
      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }

      const [productsResponse, countResponse] = await Promise.all([
        adminApi.get('/products', { params }),
        adminApi.get('/products/count', { params: { search: params.search } }),
      ]);
      if (requestId !== productRequestRef.current) return;
      const data = productsResponse.data;
      const count = countResponse.data.count;
      const nextTotalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
      if (count > 0 && currentPage > nextTotalPages) {
        setTotalProducts(count);
        setCurrentPage(nextTotalPages);
        return;
      }
      setProducts(data);
      setTotalProducts(count);
    } catch (error) {
      if (requestId === productRequestRef.current) {
        toast({ title: "Error", description: "Failed to load products", variant: "destructive" });
      }
    } finally {
      if (requestId === productRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadProducts();
    return () => { productRequestRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, sortMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        name: { en: formData.name_en, ar: formData.name_ar },
        category: { en: formData.category_en, ar: formData.category_ar },
        description: { en: formData.description_en, ar: formData.description_ar },
        price_min: parseFloat(formData.price_min),
        price_max: formData.price_max ? parseFloat(formData.price_max) : null,
        image_url: formData.image_url.trim(),
        images: normalizeImageList(galleryImages).filter((url) => url !== formData.image_url.trim()),
        specs: specs.filter(s => s.en.trim() || s.ar.trim()),
        stock_quantity: parseInt(formData.stock_quantity),
        is_active: editingProduct?.is_active ?? true,
        currency: editingProduct?.currency || 'BHD',
      };

      if (editingProduct) {
        // Update existing product
        await adminApi.put(`/products/${editingProduct.id}`, productData);
        toast({ title: "Success", description: "Product updated successfully" });
      } else {
        // Create new product
        await adminApi.post(`/products`, productData);
        toast({ title: "Success", description: "Product created successfully" });
      }

      setIsDialogOpen(false);
      resetForm();
      if (!editingProduct) {
        setCurrentPage(1);
      }
      loadProducts();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save product", variant: "destructive" });
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await adminApi.delete(`/products/${productId}`);
      toast({ title: "Success", description: "Product deleted successfully" });
      loadProducts();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete product", variant: "destructive" });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name_en: product.name.en,
      name_ar: product.name.ar,
      category_en: product.category.en,
      category_ar: product.category.ar,
      description_en: product.description.en,
      description_ar: product.description.ar,
      price_min: product.price_min.toString(),
      price_max: product.price_max ? product.price_max.toString() : '',
      image_url: product.image_url,
      stock_quantity: product.stock_quantity.toString()
    });
    setGalleryImages(getAdditionalProductImages(product));
    setGalleryImageUrl('');
    setSpecs(product.specs && product.specs.length > 0 ? [...product.specs] : []);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name_en: '',
      name_ar: '',
      category_en: '',
      category_ar: '',
      description_en: '',
      description_ar: '',
      price_min: '',
      price_max: '',
      image_url: '',
      stock_quantity: '10'
    });
    setGalleryImages([]);
    setGalleryImageUrl('');
    setSpecs([]);
  };

  const uploadGalleryFiles = async (files) => {
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const uploadedUrls = [];
      for (const file of files) {
        const data = new FormData();
        data.append('file', file);
        const response = await adminApi.post('/upload', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(`${backendUrl}${response.data.url}`);
      }
      setGalleryImages((current) => normalizeImageList(current, uploadedUrls));
      toast({ title: "Success", description: `${uploadedUrls.length} photo${uploadedUrls.length === 1 ? '' : 's'} uploaded` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload gallery photos", variant: "destructive" });
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    await uploadGalleryFiles(files);
  };

  const addGalleryImageUrl = () => {
    const url = galleryImageUrl.trim();
    if (!url) return;
    setGalleryImages((current) => normalizeImageList(current, [url]));
    setGalleryImageUrl('');
  };

  const removeGalleryImage = (url) => {
    setGalleryImages((current) => current.filter((item) => item !== url));
  };

  const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));
  const firstVisibleProduct = totalProducts === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastVisibleProduct = Math.min(currentPage * PAGE_SIZE, totalProducts);
  const pageNumbers = Array.from({ length: totalPages }, (_, idx) => idx + 1)
    .filter(page => totalPages <= 7 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (bulkView) return <AdminProductsBulk onBack={() => { setBulkView(false); loadProducts(); }} />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Products Management</h2>
          <p className="text-gray-600 mt-1">Manage your product catalog</p>
          <Button variant="outline" className="mt-3" onClick={() => setBulkView(true)}>Bulk Edit</Button>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Name (English)</Label>
                  <Input value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} required />
                </div>
                <div>
                  <Label>Name (Arabic)</Label>
                  <Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category (English)</Label>
                  <Input value={formData.category_en} onChange={(e) => setFormData({ ...formData, category_en: e.target.value })} required />
                </div>
                <div>
                  <Label>Category (Arabic)</Label>
                  <Input value={formData.category_ar} onChange={(e) => setFormData({ ...formData, category_ar: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Description (English)</Label>
                  <Textarea value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} required />
                </div>
                <div>
                  <Label>Description (Arabic)</Label>
                  <Textarea value={formData.description_ar} onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Min Price (BHD)</Label>
                  <Input type="number" step="0.001" value={formData.price_min} onChange={(e) => setFormData({ ...formData, price_min: e.target.value })} required />
                </div>
                <div>
                  <Label>Max Price (Optional)</Label>
                  <Input type="number" step="0.001" value={formData.price_max} onChange={(e) => setFormData({ ...formData, price_max: e.target.value })} />
                </div>
                <div>
                  <Label>Stock Quantity</Label>
                  <Input type="number" value={formData.stock_quantity} onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })} required />
                </div>
              </div>
              <ImageUpload label="Primary Product Image" value={formData.image_url} onChange={(url) => setFormData({ ...formData, image_url: url })} />
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label>Additional Product Photos</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => galleryFileInputRef.current?.click()}
                    disabled={galleryUploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {galleryUploading ? 'Uploading...' : 'Upload Photos'}
                  </Button>
                  <input
                    ref={galleryFileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryFileChange}
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    value={galleryImageUrl}
                    onChange={(e) => setGalleryImageUrl(e.target.value)}
                    placeholder="https://..."
                    data-testid="gallery-image-url-input"
                  />
                  <Button type="button" variant="outline" onClick={addGalleryImageUrl}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
                {galleryImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {galleryImages.map((url, index) => (
                      <div key={`${url}-${index}`} className="relative rounded-md border bg-gray-50 p-2">
                        <img src={url} alt={`Gallery ${index + 1}`} className="h-24 w-full rounded object-cover" />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(url)}
                          className="absolute -right-2 -top-2 rounded-full bg-red-600 p-1 text-white hover:bg-red-700"
                          aria-label="Remove gallery photo"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Specs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Specs / Features</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSpecs([...specs, { en: '', ar: '' }])} data-testid="add-spec-btn">
                    <Plus className="h-3 w-3 mr-1" />Add Spec
                  </Button>
                </div>
                {specs.length === 0 && <p className="text-xs text-gray-400">No specs added. Click "Add Spec" to add features like screen size, RAM, etc.</p>}
                <div className="space-y-2">
                  {specs.map((spec, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        placeholder="English (e.g. 10 Inch Screen)"
                        value={spec.en}
                        onChange={(e) => { const s = [...specs]; s[idx] = { ...s[idx], en: e.target.value }; setSpecs(s); }}
                        className="flex-1"
                        data-testid={`spec-en-${idx}`}
                      />
                      <Input
                        placeholder="Arabic"
                        value={spec.ar}
                        onChange={(e) => { const s = [...specs]; s[idx] = { ...s[idx], ar: e.target.value }; setSpecs(s); }}
                        className="flex-1"
                        dir="rtl"
                      />
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 shrink-0" onClick={() => setSpecs(specs.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="bg-red-600 hover:bg-red-700">Save Product</Button>
                <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="w-full sm:w-64">
            <Label htmlFor="admin-product-sort">Sort products</Label>
            <select
              id="admin-product-sort"
              value={sortMode}
              onChange={(event) => { setSortMode(event.target.value); setCurrentPage(1); }}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {Object.entries(SORT_OPTIONS).map(([value, option]) => (
                <option key={value} value={value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Showing {firstVisibleProduct}-{lastVisibleProduct} of {totalProducts} products
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      ) : (
        <>
          {products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden">
                  <div className="relative">
                    <img src={product.image_url} alt={product.name.en} className="h-48 w-full object-contain bg-gray-50 p-3" />
                    {getProductImageList(product).length > 1 && (
                      <Badge className="absolute left-3 top-3 bg-gray-900/85 text-white">
                        <Images className="h-3 w-3 mr-1" />
                        {getProductImageList(product).length}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <Badge className="mb-2">{product.category.en}</Badge>
                    <h3 className="font-bold text-lg mb-2">{product.name.en}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description.en}</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold text-red-600">
                        {product.price_max ? `${product.price_min} - ${product.price_max}` : product.price_min} BHD
                      </span>
                      <Badge variant="outline">Stock: {product.stock_quantity}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEdit(product)}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(product.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No products found.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              {pageNumbers.map((page, index) => {
                const previousPage = pageNumbers[index - 1];
                return (
                  <React.Fragment key={page}>
                    {previousPage && page - previousPage > 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <Button
                      variant={page === currentPage ? 'default' : 'outline'}
                      className={page === currentPage ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                      onClick={() => goToPage(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </Button>
                  </React.Fragment>
                );
              })}
              <Button
                variant="outline"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminProducts;
